declare const Janus: any;

export interface InitJanusFromServerParams {
  janusServer: string;
  janusSdkServer: string;
  sdkServerUserInfo: any;
  suid: string;
  videoEl: HTMLVideoElement;
  isSelected?: boolean;
  onError?: (err: any) => void;
  onStreamStart?: () => void;
  onAudioStream?: (stream: MediaStream) => void;
  onRemoteHandle?: (handle: any) => void;
}

export function initJanusForFourVehicle({
  janusServer,
  janusSdkServer,
  sdkServerUserInfo,
  suid,
  videoEl,
  onError,
  onStreamStart,
  onAudioStream,
  onRemoteHandle,
}: InitJanusFromServerParams): Promise<void> {
  return new Promise<void>((reject) => {
    const form = new URLSearchParams();
    form.append("credential", sdkServerUserInfo.credential);

    fetch(`${janusSdkServer}/responder`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: form.toString(),
    })
      .then((res) => res.json())
      .then((response) => {
        const turn = {
          urls: response.data.turn_url,
          username: response.data.turn_user,
          credential: response.data.turn_credential,
        };

        const token = response.data.token;

        const roomList = response.data.room_list;
        const room = roomList.find((r: any) =>
          r.transmitters?.some((t: any) => t.suid === suid)
        );
        if (!room) {
          throw new Error(`SUID:${suid} に対応する room が見つかりません`);
        }
        const roomId = room.room_num;

        let currentFeedId: number | null = null;
        let remoteHandleRef: any = null;
        let janus: any = null;

        function attachSubscriber(feedId: number) {
          janus.attach({
            plugin: "janus.plugin.soliton.videoroom",
            success: (remoteHandle: any) => {
              remoteHandleRef = remoteHandle;
              remoteHandle.send({
                message: {
                  request: "join",
                  room: roomId,
                  ptype: "subscriber",
                  feed: feedId,
                },
              });

              remoteHandle.onmessage = (_msg: any, jsep: any) => {
                if (jsep) {
                  remoteHandle.createAnswer({
                    jsep,
                    media: { audioSend: false, videoSend: false },
                    success: (jsep: any) => {
                      remoteHandle.send({
                        message: { request: "start", room: roomId },
                        jsep,
                      });
                    },
                    error: (err: any) => {
                      console.error("WebRTC error", err);
                      onError?.(err);
                    },
                  });
                }
              };

              remoteHandle.onremotestream = (stream: MediaStream) => {
                try {
                  if (videoEl.srcObject !== stream) {
                    videoEl.srcObject = stream;
                  }

                  onStreamStart?.();

                  videoEl.muted = true;
                  videoEl.playsInline = true;
                  videoEl.play();

                  onAudioStream?.(stream);
                  onRemoteHandle?.(remoteHandle);
                } catch (e) {
                  console.error("ストリーム設定エラー:", e);
                }
              };
            },
            error: (err: any) => {
              console.error("Error attaching subscriber plugin", err);
              onError?.(err);
              reject(err);
            },
          });
        }

        Janus.init({
          debug: "all",
          callback: () => {
            janus = new Janus({
              server: janusServer,
              iceServers: [turn],
              token: token,
              success: () => {
                // Publisherとして一旦参加
                janus.attach({
                  plugin: "janus.plugin.soliton.videoroom",
                  success: (handle: any) => {
                    handle.send({
                      message: {
                        request: "join",
                        room: roomId,
                        ptype: "publisher",
                        display: suid,
                      },
                    });
                  },
                  onmessage: (msg: any, jsep: any) => {
                    const event = msg["videoroom"];
                    if (
                      event === "event" &&
                      (msg["unpublished"] || msg["leaving"])
                    ) {
                      const targetFeedId = msg["unpublished"] || msg["leaving"];
                      if (targetFeedId === currentFeedId) {
                        if (msg["leaving"]) {
                          onError?.("noSignal");
                        } else if (msg["unpublished"]) {
                          setTimeout(() => {
                            if (
                              videoEl &&
                              videoEl.srcObject instanceof MediaStream
                            ) {
                              const mediaStream =
                                videoEl.srcObject as MediaStream;
                              if (
                                mediaStream
                                  .getTracks()
                                  .some((track) => track.readyState === "live")
                              ) {
                                console.log(
                                  "The video is still alive; ignore unpublish."
                                );
                              } else {
                                onError?.("noSignal");
                              }
                            }
                          }, 500);
                        }
                      }
                    }

                    // joined時（初回入室時）
                    if (
                      event === "joined" &&
                      Array.isArray(msg.publishers) &&
                      msg.publishers.length > 0
                    ) {
                      const publisher = msg.publishers.find(
                        (p: any) => !!p.video_codec
                      );
                      if (!publisher) {
                        console.error("映像送信者が見つかりません");
                        return;
                      }
                      const feedId = publisher.id;
                      currentFeedId = feedId;
                      attachSubscriber(feedId);
                      return;
                    }

                    // event時（新しいpublisher）
                    if (
                      event === "event" &&
                      Array.isArray(msg.publishers) &&
                      msg.publishers.length > 0
                    ) {
                      const publisher = msg.publishers.find(
                        (p: any) => !!p.video_codec
                      );
                      if (!publisher) {
                        console.error("映像送信者が見つかりません");
                        return;
                      }
                      const feedId = publisher.id;

                      if (currentFeedId !== feedId) {
                        currentFeedId = feedId;

                        if (remoteHandleRef && remoteHandleRef.hangup) {
                          try {
                            remoteHandleRef.hangup();
                          } catch (e) {}
                        }

                        attachSubscriber(feedId);
                      }
                      return;
                    }

                    // その他
                    if (jsep) {
                      // publisher側は映像送らないのでjsep処理不要
                    }
                  },
                  error: (err: any) => {
                    console.error("Publisher attach error", err);
                    onError?.(err);
                    reject(err);
                  },
                });
              },
              error: (err: any) => {
                console.error("Janus init error", err);
                onError?.(err);
                reject(err);
              },
            });
          },
        });
      })
      .catch((err) => {
        console.error("Failed to fetch server info", err);
        onError?.(err);
        reject(err);
      });
  });
}
